// reducers/users.js
import { createSlice } from "@reduxjs/toolkit"
import { parseToken } from "utils/tokenManager"
import { resolvePostLoginRedirect } from "utils/accessibleRoutes"
import {
  changePassword,
  forgotPassword,
  loginUser,
  userDetails,
  verifyLoginOtp,
  verifyOtp,
} from "../actions/authAction"
import { saLoginToTenant } from "../actions/superAdminAction"
import { issueToken } from "utils/tokenManager"
import { beginTenantLoginRedirect } from "utils/apiForbiddenRedirect"
import { resetTenantPosConnectionsAttempt } from "utils/tenantPosConnectionsAttempt"

const resolveAuthUserId = authData =>
  authData?._id ||
  authData?.id ||
  authData?.userId ||
  authData?.user?._id ||
  authData?.user?.id ||
  null

const initialState = {
  isLoggedIn: false,
  loading: false,
  error: null,
  actionType: null,
  authUser: {},
  message: null,
  forgetError: false,
  forgetSuccessMsg: false,
  verifyPasswordError: null,
  verifyPasswordStep: "VERIFY_OTP",
  varifyPasswordSuccesMsg: null,
  changePasswordError: null,
  changePasswordSuccessMsg: null,
  userRole: [],
  userId: null,
  accountType: null,
  accountScope: null,
  resetPasswordLoading: true,
  /**
   * Plan catalogue integrations for the logged-in tenant (with effective role perms).
   * Shape: Array<{ _id, code, name, nameAr, permissions: { tenant_integrations_1: bool, ... } }>
   * Populated from data.integrations on login / regenerate-token / userDetails.
   * Includes every integration on the tenant plan (not only view-permitted rows).
   * Empty for super_admin sessions.
   */
  userIntegrations: [],
  /** Plan-allowed social login providers from login / regenerate-token / userDetails. */
  planSocialLogins: { google: false, apple: false },
}

const getTenantIdFromAuthPayload = authData => {
  if (authData?.accountScope === "super_admin") {
    return null
  }
  return (
    authData?.tenantId ||
    authData?.tenant?._id ||
    authData?.tenant?.id ||
    authData?.user?.tenantId ||
    authData?.profile?.tenantId ||
    (authData?.token ? parseToken(authData.token)?.tenantId : null) ||
    null
  )
}

const TENANT_STORAGE_KEY = "tenantId"

const applyLoginSuccessState = (state, action, data, msg) => {
  localStorage.setItem("authUser", JSON.stringify(data))
  localStorage.setItem("isLoggedIn", true)
  localStorage.setItem("accountType", data?.accountType)
  localStorage.setItem("accountScope", data?.accountScope || "tenant")
  const tenantId = getTenantIdFromAuthPayload(data)
  if (tenantId) {
    localStorage.setItem(TENANT_STORAGE_KEY, tenantId)
  } else if (data?.accountScope === "super_admin") {
    // Clear before redirect resolution so stale tenantId does not force tenant routes.
    localStorage.removeItem(TENANT_STORAGE_KEY)
  }

  const afterLogin = resolvePostLoginRedirect("", data)
  const useHardRedirect = Boolean(action?.meta?.arg?.useHardRedirect)

  if (data?.token) {
    issueToken({ token: data.token })
  }

  if (useHardRedirect) {
    beginTenantLoginRedirect()
    window.location.replace(afterLogin)
    return
  }

  state.actionType = "LOGIN_SUCCESS"
  state.authUser = data
  state.isLoggedIn = true
  state.message = msg
  state.userId = resolveAuthUserId(data)
  state.userRole = data?.role
  state.accountType = data?.accountType
  state.accountScope = data?.accountScope || "tenant"
  state.userIntegrations = Array.isArray(data?.integrations)
    ? data.integrations
    : []
  resetTenantPosConnectionsAttempt()
  state.planSocialLogins =
    data?.planSocialLogins && typeof data.planSocialLogins === "object"
      ? {
          google: data.planSocialLogins.google === true,
          apple: data.planSocialLogins.apple === true,
        }
      : { google: false, apple: false }
  if (data?.token) {
    issueToken(data)
  }
  if (action?.meta?.arg?.useHardRedirect) {
    window.location.assign(afterLogin)
  } else if (action?.meta?.arg?.history) {
    action.meta.arg.history(afterLogin)
  }
}
const usersSlice = createSlice({
  name: "login",
  initialState,
  reducers: {
    clearLoginSession(state) {
      state.isLoggedIn = false
      state.authUser = {}
      state.userRole = []
      state.userId = null
      state.accountType = null
      state.accountScope = null
      state.userIntegrations = []
      state.planSocialLogins = { google: false, apple: false }
      state.error = null
      state.message = null
      state.actionType = null
      state.loading = false
      resetTenantPosConnectionsAttempt()
    },
  },
  extraReducers: builder => {
    builder
      .addCase(loginUser.pending, state => {
        state.loading = true
        state.error = null
        state.isLoggedIn = false
        state.actionType = null
        state.message = null
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false
        const { status, data, msg } = action.payload
        if (status) {
          if (data?.requires2FA) {
            state.actionType = "LOGIN_OTP_REQUIRED"
            state.message = msg
            state.isLoggedIn = false
          } else {
            applyLoginSuccessState(state, action, data, msg)
          }
        } else {
          state.actionType = "WRONG_PASSWORD"
          state.error =
            action.payload?.msg ||
            action.payload?.message ||
            "Unable to sign in. Please try again."
        }
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false
        state.error =
          action.payload?.msg ||
          action.payload?.message ||
          "Unable to sign in. Please try again."
        state.authUser = {}
        state.isLoggedIn = false
      })
      .addCase(verifyLoginOtp.pending, state => {
        state.loading = true
        state.error = null
        state.actionType = null
      })
      .addCase(verifyLoginOtp.fulfilled, (state, action) => {
        state.loading = false
        const { status, data, msg } = action.payload
        if (status && data?.token) {
          applyLoginSuccessState(state, action, data, msg)
        } else {
          state.actionType = "VERIFY_LOGIN_OTP_FAILED"
          state.error = action.payload?.msg || "OTP verification failed"
          state.isLoggedIn = false
        }
      })
      .addCase(verifyLoginOtp.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload?.msg || "OTP verification failed"
        state.isLoggedIn = false
      })
      .addCase(saLoginToTenant.pending, state => {
        state.error = null
      })
      .addCase(saLoginToTenant.fulfilled, (state, action) => {
        const { status, data, msg } = action.payload
        if (status && data?.token) {
          applyLoginSuccessState(state, action, data, msg)
        } else {
          state.error = action.payload?.msg || "Unable to login to tenant"
          state.isLoggedIn = false
        }
      })
      .addCase(saLoginToTenant.rejected, (state, action) => {
        state.error = action.payload?.msg || "Unable to login to tenant"
        state.isLoggedIn = false
      })

    // USER RELATED API
    builder
      .addCase(userDetails.pending, state => {
        state.loading = true
        state.error = null
        state.actionType = null
      })
      .addCase(userDetails.fulfilled, (state, action) => {
        state.loading = false
        const { status, data } = action.payload
        if (status) {
          const existingAuthUser = JSON.parse(localStorage.getItem("authUser") || "{}")
          const mergedAuthUser = { ...existingAuthUser, ...(data || {}) }

          if (data?.role) {
            mergedAuthUser.role = data.role
            state.userRole = data.role
          } else if (
            data?.roleId &&
            String(data.roleId) !== String(existingAuthUser?.roleId || existingAuthUser?.role?._id || "")
          ) {
            mergedAuthUser.role = null
            state.userRole = []
          } else {
            state.userRole = mergedAuthUser?.role || state.userRole
          }
          if (data?.roleId) {
            mergedAuthUser.roleId = data.roleId
          }
          if (Array.isArray(data?.integrations)) {
            mergedAuthUser.integrations = data.integrations
            state.userIntegrations = data.integrations
          }
          if (data?.planSocialLogins && typeof data.planSocialLogins === "object") {
            mergedAuthUser.planSocialLogins = {
              google: data.planSocialLogins.google === true,
              apple: data.planSocialLogins.apple === true,
            }
            state.planSocialLogins = mergedAuthUser.planSocialLogins
          }
          if (data?.accountType) {
            state.accountType = data.accountType
            mergedAuthUser.accountType = data.accountType
          }
          if (data?.accountScope) {
            state.accountScope = data.accountScope
            mergedAuthUser.accountScope = data.accountScope
          }

          localStorage.setItem("authUser", JSON.stringify(mergedAuthUser))
          state.authUser = mergedAuthUser
          const resolvedUserId = resolveAuthUserId(mergedAuthUser)
          if (resolvedUserId) {
            state.userId = resolvedUserId
          }
          if (data?.token) {
            issueToken(mergedAuthUser)
          }

          const tenantId = getTenantIdFromAuthPayload(mergedAuthUser)
          if (tenantId) {
            localStorage.setItem(TENANT_STORAGE_KEY, tenantId)
          } else if (mergedAuthUser?.accountScope === "super_admin") {
            localStorage.removeItem(TENANT_STORAGE_KEY)
          }
        }
      })
      .addCase(userDetails.rejected, (state, action) => {
        state.loading = false
        // Fail closed: if refresh/regenerate fails, do not keep stale permissive role
        state.userRole = []
        state.userIntegrations = []
        state.planSocialLogins = { google: false, apple: false }
      })

    builder
      .addCase(forgotPassword.pending, state => {
        state.loading = true
        state.error = null
        state.actionType = null
        state.message = null
      })
      .addCase(forgotPassword.fulfilled, (state, action) => {
        const forgotEmail = action.meta.arg.email
        const { status } = action.payload
        if (status) {
          state.actionType = "SENT_MAIL"
          state.message = action.payload.msg
          sessionStorage.setItem("forgotEmail", forgotEmail)
          const wait = Number(action.payload?.data?.resendAfterSeconds)
          const seconds = Number.isFinite(wait) && wait > 0 ? wait : 60
          sessionStorage.setItem(
            "otpResendAvailableAt",
            String(Date.now() + seconds * 1000)
          )
          state.loading = false
          state.forgetSuccessMsg = action.payload.msg
          action.meta.arg.history("/verify-password")
        } else {
          state.actionType = "SENT_MAIL_FAILED"
          state.error = action.payload.msg
          state.loading = false
        }
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        state.loading = false
        state.forgetError = action.payload.msg
      })
    builder
      .addCase(verifyOtp.pending, state => {
        state.loading = true
        state.error = null
        state.actionType = null
        state.message = null
      })
      .addCase(verifyOtp.fulfilled, (state, action) => {
        state.loading = false
        const { status } = action.payload
        if (status) {
          state.actionType = "VERIFIED_OTP"
          state.verifyPasswordStep = "NEW_PASSWORD"
          state.message = action.payload.msg
        } else {
          state.actionType = "VERIFIED_OTP_FAILED"
          state.error = action.payload.msg
          state.loading = false
        }
      })
      .addCase(verifyOtp.rejected, (state, action) => {
        state.loading = false
        state.verifyPasswordError = action.payload.msg
      })
    builder
      .addCase(changePassword.pending, state => {
        state.loading = true
        state.error = null
        state.actionType = null
        state.message = null
      })
      .addCase(changePassword.fulfilled, (state, action) => {
        state.loading = false
        const { status } = action.payload
        if (status) {
          state.actionType = "NEW_PASSWORD_SUCCESS"
          state.verifyPasswordStep = "NEW_PASSWORD"
          action.meta.arg.history("/login")
          state.message = action.payload.msg
        } else {
          state.actionType = "NEW_PASSWORD_FAILED"
          state.error = action.payload.msg
        }
      })
      .addCase(changePassword.rejected, (state, action) => {
        state.loading = false
        state.changePasswordError = action.payload.msg
      })
  },
})

export const { clearLoginSession } = usersSlice.actions

export default usersSlice.reducer
